import { DatePipe } from '@angular/common';
import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminButtonComponent,
  AdminConfirmDialogService,
  AdminEmptyStateComponent,
  AdminTextareaComponent,
} from '../../../../shared/admin';
import type { FeatureRequestComment } from '../../../../shared/models/feature-request.models';
import { FeatureRequestsService } from '../../../../shared/services/feature-requests.service';

@Component({
  selector: 'app-feature-request-comments-card',
  standalone: true,
  host: {
    class: 'fr-comments-host',
  },
  imports: [
    DatePipe,
    FormsModule,
    AdminButtonComponent,
    AdminEmptyStateComponent,
    AdminTextareaComponent,
  ],
  templateUrl: './feature-request-comments-card.component.html',
  styleUrl: './feature-request-comments-card.component.scss',
})
export class FeatureRequestCommentsCardComponent implements OnInit {
  private readonly service = inject(FeatureRequestsService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);

  readonly requestId = input.required<string>();
  readonly currentUserId = input.required<string>();
  readonly isAdmin = input(false);

  readonly changed = output<void>();

  readonly comments = signal<FeatureRequestComment[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly newComment = signal('');
  readonly editingId = signal<string | null>(null);
  readonly editText = signal('');

  ngOnInit(): void {
    this.load();
  }

  reload(): void {
    this.load();
  }

  canModify(comment: FeatureRequestComment): boolean {
    return this.isAdmin() || comment.createdBy === this.currentUserId();
  }

  startEdit(comment: FeatureRequestComment): void {
    this.editingId.set(comment._id);
    this.editText.set(comment.comment);
  }

  cancelEdit(): void {
    this.editingId.set(null);
    this.editText.set('');
  }

  async saveEdit(commentId: string): Promise<void> {
    const text = this.editText().trim();
    if (!text) return;
    this.saving.set(true);
    this.error.set(null);
    this.service.updateComment(this.requestId(), commentId, text).subscribe({
      next: () => {
        this.editingId.set(null);
        this.load();
        this.changed.emit();
        this.saving.set(false);
      },
      error: () => {
        this.error.set('Could not update comment');
        this.saving.set(false);
      },
    });
  }

  async deleteComment(comment: FeatureRequestComment): Promise<void> {
    const confirmed = await this.confirmDialog.confirm({
      title: 'Delete comment?',
      message: 'This comment will be removed from the list but recorded in the activity timeline.',
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    this.service.deleteComment(this.requestId(), comment._id).subscribe({
      next: () => {
        this.load();
        this.changed.emit();
      },
      error: () => this.error.set('Could not delete comment'),
    });
  }

  postComment(): void {
    const text = this.newComment().trim();
    if (!text) return;
    this.saving.set(true);
    this.error.set(null);
    this.service.addComment(this.requestId(), text).subscribe({
      next: () => {
        this.newComment.set('');
        this.load();
        this.changed.emit();
        this.saving.set(false);
      },
      error: () => {
        this.error.set('Could not post comment');
        this.saving.set(false);
      },
    });
  }

  private load(): void {
    this.loading.set(true);
    this.service.listComments(this.requestId()).subscribe({
      next: (items) => {
        this.comments.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Could not load comments');
        this.loading.set(false);
      },
    });
  }
}
